import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, Star, Flag, EyeOff, Eye } from 'lucide-react';
import { adminApi } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import TranslatorProfileView from '../../components/dashboard/TranslatorProfileView';
import DashModal from '../../components/dashboard/DashModal';
import { StarsDisplay } from '../../components/common/Stars';
import './AdminHome.css';
import './AdminTranslatorProfilePage.css';

const RECOMMEND_LABELS = { yes: 'Recommande', no: 'Ne recommande pas', unsure: 'Indécis' };

function TranslatorReviews({ translatorId }) {
  const [reviews, setReviews] = useState(null);
  const [moderation, setModeration] = useState(null); // { action: 'hide'|'publish', review }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => adminApi.clientReviews({ translator: translatorId })
    .then((res) => setReviews(res.reviews || []))
    .catch(() => setReviews([]));

  useEffect(() => {
    adminApi.clientReviews({ translator: translatorId })
      .then((res) => setReviews(res.reviews || []))
      .catch(() => setReviews([]));
  }, [translatorId]);

  const runModeration = async () => {
    if (!moderation) return;
    setBusy(true);
    try {
      await adminApi.moderateClientReview(moderation.review.id, {
        action: moderation.action,
        moderationNote: note,
      });
      setModeration(null);
      setNote('');
      reload();
    } catch {
      setModeration(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="atp-card" style={{ marginTop: 16 }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem', margin: '4px 0 12px' }}>
        <Star size={16} /> Avis des clients
        {reviews && <span className="orders-panel-count">{reviews.length}</span>}
      </h2>
      {reviews === null ? (
        <p style={{ color: 'var(--dash-muted)' }}>Chargement…</p>
      ) : reviews.length === 0 ? (
        <p style={{ color: 'var(--dash-muted)' }}>Aucun avis client pour ce professionnel.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reviews.map((r) => (
            <div
              key={r.id}
              style={{
                border: `1px solid ${r.flagged ? '#fdba74' : 'var(--gray-100)'}`,
                borderRadius: 10, padding: '10px 12px',
                background: r.status === 'hidden' ? 'var(--gray-50)' : r.flagged ? '#fff7ed' : 'var(--white)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <StarsDisplay value={r.rating} size={13} showValue />
                  <strong style={{ fontSize: '0.8rem' }}>{r.orderCode}</strong>
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                  {r.clientName} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString('fr-FR') : ''}
                </span>
              </div>
              {r.comment && (
                <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: 'var(--gray-700)', lineHeight: 1.5 }}>
                  {r.comment}
                </p>
              )}
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>
                  {RECOMMEND_LABELS[r.recommend] || ''}
                  {r.status === 'hidden' && (
                    <span style={{ color: '#b91c1c', fontWeight: 700 }}> · Masqué</span>
                  )}
                  {r.status === 'hidden' && r.moderationNote ? ` — ${r.moderationNote}` : ''}
                </span>
                {r.status === 'hidden' ? (
                  <button
                    type="button"
                    className="dash-btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#047857' }}
                    onClick={() => { setNote(''); setModeration({ action: 'publish', review: r }); }}
                  >
                    <Eye size={12} /> Republier
                  </button>
                ) : (
                  <button
                    type="button"
                    className="dash-btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b91c1c' }}
                    onClick={() => { setNote(''); setModeration({ action: 'hide', review: r }); }}
                  >
                    <EyeOff size={12} /> Masquer
                  </button>
                )}
              </div>
              {r.flagged && (
                <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#c2410c', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Flag size={11} /> Signalé par le professionnel{r.flagReason ? ` : ${r.flagReason}` : ''}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <DashModal
        open={Boolean(moderation)}
        title={moderation?.action === 'hide' ? 'Masquer cet avis' : 'Republier cet avis'}
        subtitle={moderation ? `${moderation.review.orderCode} — ${moderation.review.rating}/5` : ''}
        onClose={() => !busy && setModeration(null)}
      >
        <div className="dash-form" style={{ maxWidth: '100%' }}>
          {moderation?.action === 'hide' && (
            <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
              L’avis ne sera plus affiché publiquement et ne comptera plus dans la note moyenne.
            </p>
          )}
          <div className="dash-field">
            <label>Motif de modération (conservé dans l’historique)</label>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="dash-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={runModeration}>
              {busy ? 'Traitement…' : moderation?.action === 'hide' ? 'Masquer' : 'Republier'}
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setModeration(null)} disabled={busy}>
              Annuler
            </button>
          </div>
        </div>
      </DashModal>
    </div>
  );
}

export default function AdminTranslatorProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const a = t.dashboard?.admin || {};
  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setBusy(true);
    adminApi.getTranslator(id)
      .then((res) => setUser(res.translator))
      .catch(() => setError(a.notFound || 'Traducteur non trouvé'))
      .finally(() => setBusy(false));
  }, [id, a]);

  if (busy) {
    return (
      <div className="atp-page">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--dash-muted)' }}>
          <Loader size={22} className="spin" />
          <span>Chargement du profil…</span>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="atp-page">
        <button type="button" className="atp-back" aria-label={a.back || 'Retour'} onClick={() => navigate('/admin/translators')}>
          <ArrowLeft size={20} />
        </button>
        <div className="atp-card">
          <p>{error || a.notFound || 'Traducteur non trouvé'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="atp-page">
      <button type="button" className="atp-back" aria-label={a.back || 'Retour'} onClick={() => navigate('/admin/translators')}>
        <ArrowLeft size={20} />
      </button>
      <div className="atp-card">
        <TranslatorProfileView user={user} />
      </div>
      <TranslatorReviews translatorId={id} />
    </div>
  );
}
